import { FC, useContext, useEffect, useState } from "react";
import { Button, Dialog, DialogActions } from "@mui/material";
import { useTranslation } from "react-i18next";

import KmosDialogAllocate from "./KmosDialogAllocate";
import KmosDialogHeader from "./KmosDialogHeader";
import KmosDialogTotals from "./KmosDialogTotals";

import KmosDialogLanes from "./KmosDialogLanes";
import KmosDialogLanes_WithDAS from "./KmosDialogLanes_WithDAS";

import {
  api,
  IKmosV2AllocationView,
  IKmosV2TimeSlotGraphDataResponse,
  IKmosV2TimeSlotDateAllocationsResponse,
  IKmosV2TimeSlotDateSummaryResponse,
} from "../../../logic/api";
import { useReleaseManagement, ReleaseFlag } from "../../../utility/feature-management";
import { usePreferences } from "../../../utility/usePreferences";
import { useDate } from "../../../utility/useDate";
import { SessionContext } from "../../../utility/useSession";
import KmosDialogGraph from "./KmosDialogGraph";

const KmosTimeSlotDialog: FC<{
  tsView: IKmosV2AllocationView;
  isOpen: boolean;
  handleClose: () => void;
}> = ({ isOpen, handleClose, tsView }) => {
  const session = useContext(SessionContext);
  const { context } = session.current;
  const { t } = useTranslation();

  const getTimeSlotDateSummaryAPI = api.hooks.kmosV2.getTimeSlotDateSummary();
  const getTimeSlotDateAllocationsAPI = api.hooks.kmosV2.getTimeSlotDateAllocations();
  const getTimeSlotGraphDataAPI = api.hooks.kmosV2.getTimeSlotGraphData();

  const isDASAllocationsReleased = useReleaseManagement(ReleaseFlag.releaseIngestDASAllocations);

  const [summary, setSummary] = useState<IKmosV2TimeSlotDateSummaryResponse>();
  const [timeSlotAllocations, setTimeSlotAllocations] = useState<
    IKmosV2TimeSlotDateAllocationsResponse[] | null
  >([]);
  const [timeSlotGraphData, setTimeSlotGraphData] = useState<IKmosV2TimeSlotGraphDataResponse>({ graphData: [] });
  const { pickingOffset } = usePreferences();
  const { localizeDate, toFormattedFullDateTimeString } = useDate();

  const { timeSlotDate } = tsView;

  const loadData = () => {
    if (!tsView) {
      return;
    }

    getSummary();
    getAllocationsForTimeSlot();
    getTimeSlotGraphData();
  };

  const onLaneAllocated = () => {
    loadData();
  };

  useEffect(() => {
    loadData();
  }, [tsView]);

  useEffect(() => {
    setSummary(undefined);
    setTimeSlotAllocations(null);
  }, [isOpen]);

  const onTimeSlotDateLanesChange = () => {
    getSummary();
    getAllocationsForTimeSlot();
  };

  const getSummary = async () => {
    getTimeSlotDateSummaryAPI.send(context, { timeSlotDate }, (result) => {
      if (result) {
        setSummary(result);
      }
    });
  };

  const getTimeSlotGraphData = async () => {
    getTimeSlotGraphDataAPI.send(context, { timeSlotDate }, result => {
      if (result) {
        setTimeSlotGraphData(result);
      }
    });
  };

  const getAllocationsForTimeSlot = async () => {
    getTimeSlotDateAllocationsAPI.send(context, { timeSlotDate }, (result) => {
      if (result) {
        setTimeSlotAllocations(result);
      }
    });
  };

  const getPickByTime = () => {
    const localReturnDate = localizeDate(tsView.timeSlotDate.date);
    const [hours, minutes] = tsView.timeSlot.startTime
      .split(":")
      .map((val) => parseInt(val));
    const localReturnDateTime = localReturnDate.set({
      hour: hours,
      minute: minutes,
    });

    return toFormattedFullDateTimeString(
      localReturnDateTime.minus({ hours: pickingOffset }).toString()
    );
  };

  return (
    <Dialog open={isOpen} PaperProps={{
      sx: {
        p: 2,
        minWidth: 640,
        gap: 2
      }
    }}>
      <KmosDialogHeader
        date={timeSlotDate.date}
        title={tsView.timeSlot.title}
        pickByTime={getPickByTime()}
        startTime={tsView.timeSlot.startTime}
        endTime={tsView.timeSlot.endTime}
      />
      <KmosDialogTotals
        dueIn={summary?.dueIn}
        dueInTotal={summary?.totalDueIn}
        capacity={summary?.spaces}
        hasAllocations={
          timeSlotAllocations !== null &&
          timeSlotAllocations.some((a) => a.allocation.active)
        }
      />
      {!!timeSlotGraphData.graphData.length && <KmosDialogGraph graphData={timeSlotGraphData} />}
      {isDASAllocationsReleased ? (
        <KmosDialogLanes_WithDAS
          timeSlotAllocations={timeSlotAllocations}
          onChange={() => onTimeSlotDateLanesChange()}
        />
      ) : (
        <KmosDialogLanes
          timeSlotAllocations={timeSlotAllocations}
          onChange={() => onTimeSlotDateLanesChange()}
        />
      )}
      <KmosDialogAllocate
        date={timeSlotDate.date}
        letter={timeSlotDate.timeSlot}
        onLaneAllocated={onLaneAllocated}
      />
      <DialogActions sx={{ position: 'sticky', bottom: 0, right: 0 }}>
        <Button
          color="primary"
          onClick={() => handleClose()}
        >
          {t("general.done")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default KmosTimeSlotDialog;
